/**
 * Utilitários de "saúde" das cargas de dados.
 *
 * Centraliza a leitura do estado de uma carga a partir do `status`
 * (ativo/apenasLeitura) somado ao resultado da última sincronização.
 * Usado tanto pela tela de Cargas quanto pelo aviso da Home, garantindo
 * que a noção de "carga quebrada" seja idêntica nos dois lugares.
 */

/**
 * Uma carga é considerada ativa quando está marcada como ativa ou quando é
 * apenas-leitura (cargas de sistema, que não desligam).
 */
export function ehAtiva(carga) {
  if (!carga) {
    return false;
  }
  return +carga.apenasLeitura === 1 || +carga.ativo === 1;
}

/**
 * A carga já executou alguma vez? Não depende só do `id`: aceita também data
 * de fim/início, pois dependendo de como a carga é serializada o `id` pode
 * não vir — e sem isso toda carga cairia em "nunca executada" mesmo tendo
 * falhado.
 *
 * @param {object|null} ultima registro de última sincronização
 */
export function jaExecutou(ultima) {
  return !!(ultima && (ultima.id || ultima.fim || ultima.inicio));
}

/**
 * Estado de saúde de uma carga:
 *  - 'inativa'  -> carga desligada
 *  - 'quebrada' -> ativa e última sincronização falhou
 *  - 'sucesso'  -> ativa e última sincronização concluiu com sucesso
 *  - 'nunca'    -> ativa, mas nunca sincronizada
 *
 * @returns {'inativa'|'quebrada'|'sucesso'|'nunca'}
 */
export function statusOf(carga) {
  if (!ehAtiva(carga)) {
    return 'inativa';
  }

  const ultima = carga && carga.ultimaSincronizacao;

  if (jaExecutou(ultima)) {
    return ultima.sucesso ? 'sucesso' : 'quebrada';
  }

  return 'nunca';
}

/**
 * Conta quantas sincronizações falharam em sequência a partir da mais recente.
 * Recebe o histórico já carregado (ordenado da mais recente para a mais antiga).
 *
 * @param {Array<{sucesso: boolean}>} historico
 * @returns {number}
 */
export function falhasSeguidas(historico) {
  if (!Array.isArray(historico)) {
    return 0;
  }

  let n = 0;
  for (const item of historico) {
    if (item && !item.sucesso) {
      n++;
    } else {
      break;
    }
  }

  return n;
}

/**
 * Aplica um filtro de status (chip) sobre uma carga.
 *
 * 'sucesso' e 'quebradas' usam a MESMA régua de `statusOf` (portanto exigem
 * carga ativa), garantindo que contagens de chips, faixa de atenção e badges
 * nunca divirjam entre si.
 *
 * @param {object} carga
 * @param {'todas'|'ativas'|'inativas'|'sucesso'|'quebradas'} chave
 * @returns {boolean}
 */
export function matchFilter(carga, chave) {
  switch (chave) {
    case 'ativas':
      return ehAtiva(carga);
    case 'inativas':
      return !ehAtiva(carga);
    case 'sucesso':
      return statusOf(carga) === 'sucesso';
    case 'quebradas':
      return statusOf(carga) === 'quebrada';
    case 'todas':
    default:
      return true;
  }
}

/**
 * Peso de ordenação por prioridade de atenção: quebrada -> nunca -> sucesso -> inativa.
 */
const PESO_PRIORIDADE = {
  quebrada: 0,
  nunca: 1,
  sucesso: 2,
  inativa: 3,
};

export function pesoPrioridade(carga) {
  return PESO_PRIORIDADE[statusOf(carga)] ?? 9;
}

/**
 * Tempo relativo amigável ("agora", "há 5 min", "há 2 h", "há 3 dias").
 *
 * @param {string|Date} timestamp data/hora (formato 'Y-m-d H:i:s' ou Date)
 * @returns {string}
 */
export function relTime(timestamp) {
  if (!timestamp) {
    return '';
  }

  const data =
    timestamp instanceof Date
      ? timestamp
      : new Date(String(timestamp).replace(' ', 'T'));

  if (isNaN(data.getTime())) {
    return '';
  }

  const segundos = Math.floor((Date.now() - data.getTime()) / 1000);

  if (segundos < 0) {
    return 'agora';
  }
  if (segundos < 60) {
    return 'agora há pouco';
  }

  const minutos = Math.floor(segundos / 60);
  if (minutos < 60) {
    return `há ${minutos} min`;
  }

  const horas = Math.floor(minutos / 60);
  if (horas < 24) {
    return `há ${horas} h`;
  }

  const dias = Math.floor(horas / 24);
  if (dias < 30) {
    return `há ${dias} ${dias === 1 ? 'dia' : 'dias'}`;
  }

  const meses = Math.floor(dias / 30);
  if (meses < 12) {
    return `há ${meses} ${meses === 1 ? 'mês' : 'meses'}`;
  }

  const anos = Math.floor(meses / 12);
  return `há ${anos} ${anos === 1 ? 'ano' : 'anos'}`;
}
